import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { UtilityService } from '../services/utility.service';
import Swal from 'sweetalert2';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UserService } from '../services/user.service';

@Component({
  selector: 'app-enrollcustomer',
  templateUrl: './enrollcustomer.component.html',
  styleUrls: ['./enrollcustomer.component.scss']
})
export class EnrollCustomerComponent implements OnInit {

  enrollForm!: FormGroup;
  confirmData: any;
  officeid: any;
  isDomOrFemd: boolean = false;

  customerTypes: any[] = [
    { id: 1, name: 'Residential' },
    { id: 2, name: 'Multifamily' },
    { id: 3, name: 'Non-Residential' }
  ];

  usStates: string[] = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
    'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho',
    'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana',
    'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
    'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada',
    'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
    'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
    'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
    'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
    'West Virginia', 'Wisconsin', 'Wyoming'
  ];

  constructor(
    private fb: FormBuilder,
    private utilityService: UtilityService,
    private router: Router,
    private spinner: NgxSpinnerService,
    private userService: UserService
  ) { }

  ngOnInit(): void {
    this.isDomOrFemd = this.userService.IsCustomUiChange;
    this.confirmData = history.state.data;
    this.officeid = localStorage.getItem('officeId');

    this.buildForm();
    Swal.fire({
      icon: "info",
      title: "Are you sure you want to add this customer manually?",
      text: "This cannot be undone",
      confirmButtonColor: "#37474f"
    });
  }

  private buildForm(): void {
    this.enrollForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      accountNumber: ['', Validators.required],
      primaryPhone: ['', Validators.required],
      alternatePhone: [''],
      email: ['', [Validators.required, Validators.email,
      Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$')]],
      customerType: ['', Validators.required],
      companyName: [''],

      serviceAddress: this.fb.group({
        streetNumber: ['', Validators.required],
        streetName: ['', Validators.required],
        apartment: [''],
        city: ['', Validators.required],
        state: ['', Validators.required],
        zip: [
          '', 
          [
            Validators.required,
            Validators.pattern('^\\d{5}(-\\d{4})?$')
          ]
        ]
      }),

      sameAsService: [false],

      mailingAddress: this.fb.group({
        streetNumber: ['', Validators.required],
        streetName: ['', Validators.required],
        apartment: [''],
        city: ['', Validators.required],
        state: ['', Validators.required],
        zip: ['', [Validators.required,
          Validators.pattern('^\\d{5}(-\\d{4})?$')
        ]]
      })
    });
  }

  allowOnlyNumbers(event: KeyboardEvent) {
    if (!this.isDomOrFemd) return;

    const charCode = event.which ? event.which : event.keyCode;
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
    }
  }

  onSameAddressChange(): void {
    const same = this.enrollForm.get('sameAsService')?.value;
    const service = this.enrollForm.get('serviceAddress')?.value;
    const mailingGroup = this.enrollForm.get('mailingAddress');

    if (same) {
      mailingGroup?.patchValue(service);
      mailingGroup?.disable();
    } else {
      mailingGroup?.enable();
    }
  }

  createCustomer(): void {
    if (this.enrollForm.invalid) {
      this.enrollForm.markAllAsTouched();
      return;
    }

    Swal.fire({
      title: 'Entering a customer manually cannot be reversed',
      text: 'Please ensure the information entered is accurate and complete before submitting',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#37474f',
      cancelButtonColor: '#f44336',
      confirmButtonText: 'Yes, create the customer',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (!result.value) {
        return;
      }

      this.spinner.show();

      const formValue = this.enrollForm.getRawValue();

      const bodyContent = {
        Firstname: formValue.firstName,
        LastName: formValue.lastName,
        CompanyName: formValue.companyName,
        AccountNumber: formValue.accountNumber,

        ServiceStreetnumber: formValue.serviceAddress.streetNumber,
        ServiceStreetName: formValue.serviceAddress.streetName,
        ServiceAppartment: formValue.serviceAddress.apartment,
        ServiceCity: formValue.serviceAddress.city,
        ServiceState: formValue.serviceAddress.state,
        ServiceZipCode: formValue.serviceAddress.zip,

        MailStreetNumber: formValue.mailingAddress.streetNumber,
        MailStreetName: formValue.mailingAddress.streetName,
        MailAppartment: formValue.mailingAddress.apartment,
        MailCity: formValue.mailingAddress.city,
        MailState: formValue.mailingAddress.state,
        MailZip: formValue.mailingAddress.zip,

        PrimaryPhone: formValue.primaryPhone,
        AlternatePhone: formValue.alternatePhone,
        Email: formValue.email,
        CustomerType: formValue.customerType
      };

      this.utilityService.enrollCustomer(this.officeid, bodyContent).subscribe({
        next: (res: any) => {
          this.spinner.hide();

          if (res.Action === 1) { // 1 indicates no account was added
            Swal.fire({
              icon: "info",
              html: '<div style="text-align:center"> ' + res.Message + '</div>',
              width: '800px',
              confirmButtonText: 'OK',
            }).then((result) => {
              if (result.value)
                this.router.navigate(['/enrollcustomer'], { state: { data: this.confirmData } });
            });
          } else if (res.Action === 0) { // 0 indicates an account was successfully added
            Swal.fire({
              icon: "success",
              html: '<div style="text-align:center"> ' + res.Message + '</div>',
              width: '800px',
              confirmButtonText: 'OK',
            }).then((result) => {
              if (result.value) {
                localStorage.setItem('newAccountNumber', bodyContent.AccountNumber);
                localStorage.setItem('personcompanyid', res.Personcompany_id);
                this.router.navigate(['/search'], { state: { data: this.confirmData } });
              }
            });
          } else {
            this.router.navigate(['/search'], { state: { data: this.confirmData } });
          }
        },
        error: () => {
          this.spinner.hide();
        }
      });
    });
  }
}
